/**
 * Athletes Launch — SessionGuardProvider
 *
 * React component that enforces idle session timeout.
 * Mount this inside the authenticated section of your app tree.
 *
 * When the user is idle for 30 minutes, a branded warning modal appears.
 * At 35 minutes, onSignOut() is called automatically.
 *
 * Usage (in UserProvider or App.js):
 *   import SessionGuardProvider from 'components/shared/SessionGuardProvider';
 *
 *   <SessionGuardProvider onSignOut={logout}>
 *     {children}
 *   </SessionGuardProvider>
 */
import React, { useEffect, useState, useCallback } from 'react';
import { createSessionGuard } from '../../utils/sessionGuard';

const COUNTDOWN_SECONDS = 5 * 60; // 5 minutes to act before auto-logout

export default function SessionGuardProvider({ onSignOut, children }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown]     = useState(COUNTDOWN_SECONDS);
  const [guardRef, setGuardRef]       = useState(null);

  const handleExpire = useCallback(() => {
    setShowWarning(false);
    if (typeof onSignOut === 'function') onSignOut();
  }, [onSignOut]);

  const handleWarn = useCallback(() => {
    setShowWarning(true);
    setCountdown(COUNTDOWN_SECONDS);
  }, []);

  // Mount the session guard
  useEffect(() => {
    const guard = createSessionGuard({ onWarn: handleWarn, onExpire: handleExpire });
    guard.start();
    setGuardRef(guard);
    return () => guard.stop();
  }, [handleWarn, handleExpire]);

  // Count down when warning is visible
  useEffect(() => {
    if (!showWarning) return;
    if (countdown <= 0) { handleExpire(); return; }
    const tick = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(tick);
  }, [showWarning, countdown, handleExpire]);

  const handleStaySignedIn = () => {
    setShowWarning(false);
    setCountdown(COUNTDOWN_SECONDS);
    if (guardRef) guardRef.resetTimer();
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}

      {showWarning && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Session timeout warning">
          <div style={styles.modal}>
            {/* Icon */}
            <div style={styles.iconRing}>
              <span style={styles.iconEmoji}>⏱</span>
            </div>

            {/* Title */}
            <h2 style={styles.title}>Still there?</h2>

            {/* Body */}
            <p style={styles.body}>
              To protect your account, you'll be signed out in{' '}
              <strong style={styles.countdown}>{formatTime(countdown)}</strong>
              {' '}due to inactivity.
            </p>

            {/* Progress bar */}
            <div style={styles.progressTrack}>
              <div
                style={{
                  ...styles.progressBar,
                  width: `${(countdown / COUNTDOWN_SECONDS) * 100}%`,
                }}
              />
            </div>

            {/* Actions */}
            <button
              id="session-guard-stay-signed-in"
              style={styles.primaryBtn}
              onClick={handleStaySignedIn}
            >
              Stay Signed In
            </button>
            <button
              id="session-guard-sign-out"
              style={styles.ghostBtn}
              onClick={handleExpire}
            >
              Sign Out Now
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    background: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 100%)',
    border: '1px solid rgba(99, 102, 241, 0.25)',
    borderRadius: 24,
    padding: '48px 40px 36px',
    maxWidth: 420,
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)',
  },
  iconRing: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(139,92,246,0.2))',
    border: '1px solid rgba(99,102,241,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  iconEmoji: { fontSize: 32 },
  title: {
    color: '#f1f5f9',
    margin: '0 0 16px',
    fontSize: 24,
    fontWeight: 700,
    fontFamily: 'Orbitron, sans-serif',
    letterSpacing: '0.02em',
  },
  body: {
    color: 'rgba(148, 163, 184, 0.9)',
    lineHeight: 1.7,
    marginBottom: 24,
    fontSize: 15,
  },
  countdown: {
    color: '#f59e0b',
    fontFamily: 'Orbitron, monospace',
    fontWeight: 700,
  },
  progressTrack: {
    height: 4,
    background: 'rgba(255,255,255,0.08)',
    borderRadius: 4,
    marginBottom: 28,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
    borderRadius: 4,
    transition: 'width 1s linear',
  },
  primaryBtn: {
    display: 'block',
    width: '100%',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: '#fff',
    border: 'none',
    borderRadius: 14,
    padding: '15px 24px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    marginBottom: 12,
    letterSpacing: '0.02em',
    transition: 'opacity 0.2s',
  },
  ghostBtn: {
    display: 'block',
    width: '100%',
    background: 'transparent',
    color: 'rgba(148, 163, 184, 0.7)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: '13px 24px',
    fontSize: 14,
    cursor: 'pointer',
    transition: 'color 0.2s',
  },
};
